import apiClient from "../config/api";

const safetyObservationService = {
  /**
   * Get safety observation rounds with optional filters and pagination
   * @param {object} filters - { startDate, endDate, observationType, categoryId, plantId, statusId, page, pageSize, sortBy, sortOrder }
   * @returns {Promise<{data: Array, pagination: Object}>}
   */
  getSafetyObservationRounds: async (filters = {}) => {
    const params = {};
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.observationType)
      params.observationType = filters.observationType;
    if (filters.categoryId) params.categoryId = filters.categoryId;
    if (filters.plantId) params.plantId = filters.plantId;
    if (filters.statusId) params.statusId = filters.statusId;
    if (filters.page) params.page = filters.page;
    if (filters.pageSize) params.pageSize = filters.pageSize;
    if (filters.sortBy) params.sortBy = filters.sortBy;
    if (filters.sortOrder) params.sortOrder = filters.sortOrder;
    if (filters.searchTerm) params.searchTerm = filters.searchTerm;

    const response = await apiClient.get("/SafetyObservationRound", { params });
    return response.data;
  },

  /**
   * Get scheduled safety observation rounds
   * @param {number} year
   * @param {number} month
   * @param {number} [employeeId]
   * @returns {Promise<Array>}
   */
  getSchedules: async (year, month, employeeId, plantId) => {
    const params = { year, month };
    if (employeeId) params.employeeId = employeeId;
    if (plantId) params.plantId = plantId;
    const response = await apiClient.get(
      "/safety-observation/schedule/rounds",
      { params },
    );
    return response.data;
  },

  /**
   * Get safety observation round including items, observers, actions
   * @param {number} roundId
   * @returns {Promise<object>}
   */
  getSafetyObservationRoundById: async (roundId) => {
    const response = await apiClient.get(`/SafetyObservationRound/${roundId}`);
    return response.data;
  },

  /**
   * Create safety observation round
   * @param {object} roundData
   * @returns {Promise<object>}
   */
  createSafetyObservationRound: async (roundData) => {
    const response = await apiClient.post("/SafetyObservationRound", roundData);
    return response.data;
  },

  /**
   * Update safety observation round
   * @param {number} roundId
   * @param {object} roundData
   * @returns {Promise<object>}
   */
  updateSafetyObservationRound: async (roundId, roundData) => {
    const response = await apiClient.post(
      `/SafetyObservationRound/update/${roundId}`,
      roundData,
    );
    return response.data;
  },

  /**
   * Approve safety observation round
   * @param {number} roundId
   * @returns {Promise<object>}
   */
  approveSafetyObservationRound: async (roundId, approvalData) => {
    const response = await apiClient.post(
      `/SafetyObservationRound/${roundId}/approve`,
      approvalData
    );
    return response.data;
  },

  /**
   * Delete safety observation round
   * @param {number} roundId
   * @returns {Promise<object>}
   */
  deleteSafetyObservationRound: async (roundId) => {
    const response = await apiClient.post(
      `/SafetyObservationRound/delete/${roundId}`,
    );
    return response.data;
  },

  /**
   * Get items for a round
   * @param {number} roundId
   * @returns {Promise<Array>}
   */
  getItemsByRound: async (roundId) => {
    const response = await apiClient.get(
      `/SafetyObservationRound/items/round/${roundId}`,
    );
    return response.data;
  },

  /**
   * Update unsafe item status
   * @param {number} itemId
   * @param {string} status - "Open" or "Closed"
   * @returns {Promise<object>}
   */
  updateItemStatus: async (itemId, status) => {
    const response = await apiClient.post(
      `/SafetyObservationRound/update/items/${itemId}/status`,
      { status },
    );
    return response.data;
  },

  /**
   * Link unsafe item to CAPA
   * @param {number} itemId
   * @param {number} capaId
   * @returns {Promise<object>}
   */
  linkItemToCapa: async (itemId, capaId) => {
    const response = await apiClient.post(
      `/SafetyObservationRound/items/${itemId}/link-capa`,
      { capaId },
    );
    return response.data;
  },

  /**
   * Get all open unsafe items
   * @returns {Promise<Array>}
   */
  getOpenUnsafeItems: async () => {
    const response = await apiClient.get(
      "/SafetyObservationRound/open-unsafe-items",
    );
    return response.data;
  },

  /**
   * Create non-scheduled round
   * @param {object} data
   * @returns {Promise<object>}
   */
  createNonScheduledRound: async (data) => {
    const response = await apiClient.post(
      "/safety-observation/schedule/nonscheduled",
      data,
    );
    return response.data;
  },

  /**
   * Get trends data
   * @param {object} filters - { startDate, endDate, plantId }
   * @returns {Promise<object>}
   */
  getTrends: async (filters = {}) => {
    const params = {};
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.plantId) params.plantId = filters.plantId;

    const response = await apiClient.get("/SafetyObservationRound/trends", {
      params,
    });
    return response.data;
  },

  /**
   * Get statistics
   * @param {object} filters - { startDate, endDate, categoryId, plantId }
   * @returns {Promise<object>}
   */
  getStatistics: async (filters = {}) => {
    const params = {};
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.categoryId) params.categoryId = filters.categoryId;
    if (filters.plantId) params.plantId = filters.plantId;

    const response = await apiClient.get("/SafetyObservationRound/statistics", {
      params,
    });
    return response.data;
  },

  /**
   * Get all categories with subcategories
   * @returns {Promise<Array>}
   */
  getCategories: async () => {
    const response = await apiClient.get("/SafetyBehaviorCategory");
    return response.data;
  },

  /**
   * Get subcategories for a category
   * @param {number} categoryId
   * @returns {Promise<Array>}
   */
  getSubCategories: async (categoryId) => {
    const response = await apiClient.get(
      `/SafetyBehaviorCategory/${categoryId}/subcategories`,
    );
    return response.data;
  },

  /**
   * Create a new category
   * @param {object} categoryData
   * @returns {Promise<object>}
   */
  createCategory: async (categoryData) => {
    const response = await apiClient.post(
      "/SafetyBehaviorCategory",
      categoryData,
    );
    return response.data;
  },

  /**
   * Update a category
   * @param {number} categoryId
   * @param {object} categoryData
   * @returns {Promise<object>}
   */
  updateCategory: async (categoryId, categoryData) => {
    const response = await apiClient.post(
      `/SafetyBehaviorCategory/update/${categoryId}`,
      categoryData,
    );
    return response.data;
  },

  /**
   * Delete a category
   * @param {number} categoryId
   * @returns {Promise<object>}
   */
  deleteCategory: async (categoryId) => {
    const response = await apiClient.post(
      `/SafetyBehaviorCategory/delete/${categoryId}`,
    );
    return response.data;
  },

  /**
   * Create a new subcategory
   * @param {number} categoryId
   * @param {object} subCategoryData
   * @returns {Promise<object>}
   */
  createSubCategory: async (categoryId, subCategoryData) => {
    const response = await apiClient.post(
      `/SafetyBehaviorCategory/${categoryId}/subcategories`,
      subCategoryData,
    );
    return response.data;
  },

  /**
   * Update a subcategory
   * @param {number} subCategoryId
   * @param {object} subCategoryData
   * @returns {Promise<object>}
   */
  updateSubCategory: async (subCategoryId, subCategoryData) => {
    const response = await apiClient.post(
      `/SafetyBehaviorCategory/update/subcategories/${subCategoryId}`,
      subCategoryData,
    );
    return response.data;
  },

  /**
   * Delete a subcategory
   * @param {number} subCategoryId
   * @returns {Promise<object>}
   */
  deleteSubCategory: async (subCategoryId) => {
    const response = await apiClient.post(
      `/SafetyBehaviorCategory/delete/subcategories/${subCategoryId}`,
    );
    return response.data;
  },

  /**
   * Get Observation Summary Report
   * @param {object} filters - { startDate, endDate, plantId, categoryId, status }
   * @returns {Promise<object>}
   */
  getObservationSummaryReport: async (filters = {}) => {
    const params = { ...filters };
    if (filters.plantIds && filters.plantIds.length > 0) params.plantIds = filters.plantIds;
    if (filters.categoryId) params.categoryId = filters.categoryId;
    if (filters.status) params.status = filters.status;

    const response = await apiClient.get(
      "/SafetyObservationRound/reports/summary",
      { 
        params,
        paramsSerializer: params => {
          const searchParams = new URLSearchParams();
          Object.keys(params).forEach(key => {
            const val = params[key];
            if (val === null || val === undefined || val === '') return;
            if (Array.isArray(val)) {
              val.forEach(v => searchParams.append(key, v));
            } else {
              searchParams.append(key, val);
            }
          });
          return searchParams.toString();
        }
      },
    );
    return response.data;
  },

  /**
   * Get Unsafe Behavior Report
   * @param {object} filters - { startDate, endDate, plantId, categoryId }
   * @returns {Promise<object>}
   */
  getUnsafeBehaviorReport: async (filters = {}) => {
    const params = { ...filters };
    if (filters.plantIds && filters.plantIds.length > 0) params.plantIds = filters.plantIds;
    if (filters.categoryId) params.categoryId = filters.categoryId;

    const response = await apiClient.get(
      "/SafetyObservationRound/reports/unsafe-behavior",
      { 
        params,
        paramsSerializer: params => {
          const searchParams = new URLSearchParams();
          Object.keys(params).forEach(key => {
            const val = params[key];
            if (val === null || val === undefined || val === '') return;
            if (Array.isArray(val)) {
              val.forEach(v => searchParams.append(key, v));
            } else {
              searchParams.append(key, val);
            }
          });
          return searchParams.toString();
        }
      },
    );
    return response.data;
  },

  /**
   * Get Safe Behavior Report
   * @param {object} filters - { startDate, endDate, plantId, categoryId }
   * @returns {Promise<object>}
   */
  getSafeBehaviorReport: async (filters = {}) => {
    const params = { ...filters };
    if (filters.plantIds && filters.plantIds.length > 0) params.plantIds = filters.plantIds;
    if (filters.categoryId) params.categoryId = filters.categoryId;

    const response = await apiClient.get(
      "/SafetyObservationRound/reports/safe-behavior",
      { 
        params,
        paramsSerializer: params => {
          const searchParams = new URLSearchParams();
          Object.keys(params).forEach(key => {
            const val = params[key];
            if (val === null || val === undefined || val === '') return;
            if (Array.isArray(val)) {
              val.forEach(v => searchParams.append(key, v));
            } else {
              searchParams.append(key, val);
            }
          });
          return searchParams.toString();
        }
      },
    );
    return response.data;
  },

  /**
   * Get Category-wise Observation Report
   * @param {object} filters - { startDate, endDate, plantId, categoryId, status }
   * @returns {Promise<object>}
   */
  getCategoryWiseReport: async (filters = {}) => {
    const params = { ...filters };
    if (filters.plantIds && filters.plantIds.length > 0) params.plantIds = filters.plantIds;
    if (filters.categoryId) params.categoryId = filters.categoryId;
    if (filters.status) params.status = filters.status;

    const response = await apiClient.get(
      "/SafetyObservationRound/reports/category-wise",
      { 
        params,
        paramsSerializer: params => {
          const searchParams = new URLSearchParams();
          Object.keys(params).forEach(key => {
            const val = params[key];
            if (val === null || val === undefined || val === '') return;
            if (Array.isArray(val)) {
              val.forEach(v => searchParams.append(key, v));
            } else {
              searchParams.append(key, val);
            }
          });
          return searchParams.toString();
        }
      },
    );
    return response.data;
  },

  /**
   * Get Area/Department-wise Report
   * @param {object} filters - { startDate, endDate, plantId, categoryId, status }
   * @returns {Promise<object>}
   */
  getAreaDepartmentWiseReport: async (filters = {}) => {
    const params = { ...filters };
    if (filters.plantIds && filters.plantIds.length > 0) params.plantIds = filters.plantIds;
    if (filters.categoryId) params.categoryId = filters.categoryId;
    if (filters.status) params.status = filters.status;

    const response = await apiClient.get(
      "/SafetyObservationRound/reports/area-department-wise",
      { 
        params,
        paramsSerializer: params => {
          const searchParams = new URLSearchParams();
          Object.keys(params).forEach(key => {
            const val = params[key];
            if (val === null || val === undefined || val === '') return;
            if (Array.isArray(val)) {
              val.forEach(v => searchParams.append(key, v));
            } else {
              searchParams.append(key, val);
            }
          });
          return searchParams.toString();
        }
      },
    );
    return response.data;
  },

  /**
   * Get Observer Performance Report
   * @param {object} filters - { startDate, endDate, plantId, categoryId, status }
   * @returns {Promise<object>}
   */
  getObserverPerformanceReport: async (filters = {}) => {
    const params = { ...filters };
    if (filters.plantIds && filters.plantIds.length > 0) params.plantIds = filters.plantIds;
    if (filters.categoryId) params.categoryId = filters.categoryId;
    if (filters.status) params.status = filters.status;

    const response = await apiClient.get(
      "/SafetyObservationRound/reports/observer-performance",
      { 
        params,
        paramsSerializer: params => {
          const searchParams = new URLSearchParams();
          Object.keys(params).forEach(key => {
            const val = params[key];
            if (val === null || val === undefined || val === '') return;
            if (Array.isArray(val)) {
              val.forEach(v => searchParams.append(key, v));
            } else {
              searchParams.append(key, val);
            }
          });
          return searchParams.toString();
        }
      },
    );
    return response.data;
  },

  /**
   * Get Action Tracking Report
   * @param {object} filters - { startDate, endDate, plantId, categoryId, status }
   * @returns {Promise<object>}
   */
  getActionTrackingReport: async (filters = {}) => {
    const params = { ...filters };
    if (filters.plantIds && filters.plantIds.length > 0) params.plantIds = filters.plantIds;
    if (filters.categoryId) params.categoryId = filters.categoryId;
    if (filters.status) params.status = filters.status;

    const response = await apiClient.get(
      "/SafetyObservationRound/reports/action-tracking",
      { 
        params,
        paramsSerializer: params => {
          const searchParams = new URLSearchParams();
          Object.keys(params).forEach(key => {
            const val = params[key];
            if (val === null || val === undefined || val === '') return;
            if (Array.isArray(val)) {
              val.forEach(v => searchParams.append(key, v));
            } else {
              searchParams.append(key, val);
            }
          });
          return searchParams.toString();
        }
      },
    );
    return response.data;
  },

  /**
   * Upload an attachment file
   * @param {File} file
   * @returns {Promise<object>} - { fileName, fileType, fileUrl }
   */
  uploadAttachment: async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await apiClient.post(
      "/SafetyObservationRound/upload",
      formData,
      {
        headers: {
          "Content-Type": undefined,
        },
      },
    );
    return response.data;
  },

  /**
   * Create Schedule Configuration
   * @param {object} configData
   * @returns {Promise<object>}
   */
  createScheduleConfig: async (configData) => {
    const response = await apiClient.post(
      "/safety-observation/schedule/config",
      configData,
    );
    return response.data;
  },

  /**
   * Get Safety Observation Settings (Configuration + Notification)
   * @returns {Promise<object>}
   */
  getSBOSettings: async () => {
    const response = await apiClient.get("/SafetyObservationSettings");
    return response.data;
  },

  /**
   * Save Safety Observation Settings (Configuration + Notification)
   * Uses upsert: one row per tenant
   * @param {object} settingsData
   * @returns {Promise<object>}
   */
  saveSBOSettings: async (settingsData) => {
    const response = await apiClient.post(
      "/SafetyObservationSettings/update",
      settingsData,
    );
    return response.data;
  },
  /**
   * Delete schedule (and potentially linked round)
   * @param {number} scheduleId
   * @returns {Promise<object>}
   */
  deleteSchedule: async (scheduleId) => {
    const response = await apiClient.post(
      `/SafetyObservationSchedule/delete/${scheduleId}`,
    );
    return response.data;
  },
};

export default safetyObservationService;
